'use client';

import * as z from 'zod';
import { CancellationPolicy } from '@/lib/enums';

export const bookingFormSchema = z.object({
  message: z.string().min(1, {
    message: 'Would you let someone in your house without any information?',
  }),

  phone: z
    .string()
    .min(1, {
      message: 'Phone number is required for booking confirmation',
    })
    .refine(
      (val) => {
        return /^\+20\d{10}$|^0\d{10}$|^[+0]\d{5,}$/.test(val);
      },
      {
        message:
          'Seriously, we need your phone number, you risk not being able to book the property',
      }
    ),
  address: z.string().min(1, {
    message: 'Address is required for booking confirmation',
  }),
  guests: z.array(
    z.object({
      name: z.string().min(1, { message: 'Name is required' }),
      age: z.preprocess(
        (val) => {
          if (typeof val === 'string') {
            const parsed = Number(val);
            return isNaN(parsed) ? val : parsed;
          }
          return val;
        },
        z.number().min(1, { message: 'Number must be at least 1' })
      ),
      gender: z.string(),
    })
  ),
  acceptedTerms: z
    .boolean({
      required_error: 'You must accept the terms and conditions to continue.',
    })
    .refine((val) => val, {
      message: 'You must accept the terms and conditions to continue.',
    }),
});

export const availabilitySchema = z.object({
  basePrice: z.number().positive().int(),
  weekendBasePrice: z.number().int().positive(),
  minWeekendStay: z.number().int().positive(),
  cleaningFee: z.number().positive().int().optional(),
  minStay: z.number().int().positive(),
  differentWeekendPrice: z.boolean(),
  bookableDates: z
    .array(
      z.object({
        price: z.number().int().positive(),
        date: z.date(),
        minStay: z.number().int(),
        isAvailable: z.boolean(),
        status: z.enum(['available', 'booked', 'on_hold']),
      })
    )
    .min(0),
});
export const formSchema = z.object({
  destination: z.string().min(2, {
    message: 'Fein? Ya3ny we need to know where the property is! (Where is it?)',
  }),
  area: z.string().min(2, {
    message:
      "Pick a compound. If it's not listed, just contact our support team — we promise they don't bite!",
  }),
  beds: z.number().int().positive(),
  baths: z.number().int().positive(),
  basePrice: z.coerce.number().int().positive({
    message: 'We can rent it for free, but are you sure?',
  }),
  minStay: z.coerce.number().int().positive({
    message: 'Can guests stay only 1 night? 2 nights? 7 days?',
  }),
  insuranceFee: z.coerce.number().int().nonnegative().optional(),
  maxGuests: z.number().int().positive(),
  location: z.string().optional(),
  pin: z.object({
    lat: z.number(),
    lng: z.number(),
  }),
  title: z
    .string({
      required_error: "It's required, ya captain.",
    })
    .min(5, {
      message: 'Ma3lesh, but your property needs a proper name.',
    }),
  perks: z.array(z.string()).min(1, {
    message: 'At least one perk, habibi! Wi-Fi, AC, anything?',
  }),
  details: z.string().min(20, {
    message: "Bas keda? (That's it?) that's too short. Tell us more, ya fandem!",
  }),
  cancelationPolicy: z.enum([
    CancellationPolicy.FLEXIBLE,
    CancellationPolicy.FIRM,
    CancellationPolicy.MODERATE,
  ]),
  checkInTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Please enter a valid time in the format HH:MM'),
  checkOutTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Please enter a valid time in the format HH:MM'),
  houseRules: z.string().min(1, {
    message: 'No rules? Begad?',
  }),
  images: z.array(z.string()).min(4, {
    message: "Pictures or it didn't happen! We need at least 4 images — show us the goods!",
  }),
  acceptedTerms: z
    .boolean({
      required_error: 'You must accept the terms and conditions to continue.',
    })
    .refine((val) => val, {
      message: 'You must accept the terms and conditions to continue.',
    }),
});

export const perks = [
  'Free Wi-Fi',
  'Air Conditioning',
  'Swimming Pool',
  'Balcony',
  'Private Beach',
  'Private Pool',
  'BBQ Area',
  'Sea View',
  'Garden',
  'Gym',
  'Pet-friendly',
  'Outdoor Seating Area',
  'Jacuzzi',
  'Washing Machine',
  'Kitchen',
  'Parking',
  'Smoking Allowed',
  'Mountain View',
] as const;
